import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Typography, useTheme } from '@mui/material';
import { Button as CommonBtn } from '@components/index';
import { fonts } from '@theme/fontsCustomer';
import { GoogleColorIcon } from '@icons/GoogleColorIcon';
import { MicrosoftIcon } from '@icons/MicrosoftIcon';

type Props = {
  showGoogleLogin: boolean;
  showMicrosoftLogin: boolean;
  /** 구분선("또는") 숨김 여부 (비밀번호 비활성화 환경 등) */
  hideDivider?: boolean;
  onGoogleLogin: () => void;
  onMicrosoftLogin: () => void;
};

/** Google/Microsoft OAuth 로그인 버튼 + 구분선 */
export const OAuthButtons = ({ showGoogleLogin, showMicrosoftLogin, hideDivider, onGoogleLogin, onMicrosoftLogin }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!showGoogleLogin && !showMicrosoftLogin) return null;

  return (
    <>
      {/* 구분선: 비밀번호 비활성화 환경에서는 숨김 */}
      {!hideDivider && (
        <Divider>
          <DividerLine />
          <DividerLabel variant="headline9" color={theme.colors['text-gray-light']}>
            {t('Login_String_01')}
          </DividerLabel>
          <DividerLine />
        </Divider>
      )}

      {/* Google 로그인 버튼 */}
      {showGoogleLogin && (
        <GoogleBtn size="2extraLarge" variant="contain" onClick={onGoogleLogin}>
          <GoogleColorIcon />
          <span>{t('Login_Google')}</span>
        </GoogleBtn>
      )}

      {/* Microsoft 로그인 버튼 */}
      {showMicrosoftLogin && (
        <MicrosoftBtn size="2extraLarge" variant="contain" onClick={onMicrosoftLogin}>
          <MicrosoftIcon />
          <span>{t('Login_Microsoft')}</span>
        </MicrosoftBtn>
      )}
    </>
  );
};

// flexbox 방식 구분선: overflow: hidden 컨테이너에서도 잘리지 않음
const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 36px;
  gap: 16px;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${({ theme }) => theme.colors['border-gray-light']};
`;

const DividerLabel = styled(Typography)`
  flex-shrink: 0;
`;

const BaseOAuthBtn = styled(CommonBtn)`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 15px 0;
  border-radius: 3px;
  border: 1px solid ${props => props.theme.colors['border-gray-dark']};
  background: ${props => props.theme.colors['bg-gray-lighter']};
  color: ${props => props.theme.colors['text-gray-main']};

  &:hover {
    background: ${props => props.theme.colors['bg-gray-lighter']};
  }
  ${fonts.Button2}

  & > span {
    gap: 8px;
  }
`;

export const GoogleBtn = styled(BaseOAuthBtn)`
  margin-top: 36px;
`;

export const MicrosoftBtn = styled(BaseOAuthBtn)`
  margin-top: 16px;
`;
